import React, { useState } from 'react';
import { MapPin, Package, DollarSign } from 'lucide-react';

export default function GeographicAnalysisPanel({ data }) {
  const [sortBy, setSortBy] = useState('revenue'); // revenue, orders, velocity

  if (!data || data?.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
        <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
          <MapPin className="w-5 h-5 text-green-600" />
          Analyse Géographique
        </h2>
        <p className="text-gray-500 text-center py-8">Aucune donnée géographique disponible</p>
      </div>
    );
  }

  const sortedData = [...data]?.sort((a, b) => {
    switch(sortBy) {
      case 'revenue': return b?.revenue - a?.revenue;
      case 'orders': return b?.orders - a?.orders;
      case 'velocity': return b?.stockVelocity - a?.stockVelocity;
      default: return 0;
    }
  });

  const totalRevenue = data?.reduce((sum, d) => sum + (d?.revenue || 0), 0);
  const totalOrders = data?.reduce((sum, d) => sum + (d?.orders || 0), 0);

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
          <MapPin className="w-5 h-5 text-green-600" />
          Analyse Géographique Performance
        </h2>
        
        <select
          value={sortBy}
          onChange={(e) => setSortBy(e?.target?.value)}
          className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-green-500 focus:border-transparent"
        >
          <option value="revenue">Trier par revenu</option>
          <option value="orders">Trier par commandes</option>
          <option value="velocity">Trier par vélocité</option>
        </select>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-green-50 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-green-700 font-medium">Revenu Total</p>
              <p className="text-2xl font-bold text-green-900 mt-1">€{totalRevenue?.toFixed(2)}</p>
            </div>
            <DollarSign className="w-8 h-8 text-green-600" />
          </div>
        </div>
        
        <div className="bg-blue-50 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-blue-700 font-medium">Total Commandes</p>
              <p className="text-2xl font-bold text-blue-900 mt-1">{totalOrders}</p>
            </div>
            <Package className="w-8 h-8 text-blue-600" />
          </div>
        </div>
        
        <div className="bg-purple-50 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-purple-700 font-medium">Zones Actives</p>
              <p className="text-2xl font-bold text-purple-900 mt-1">{data?.length}</p>
            </div>
            <MapPin className="w-8 h-8 text-purple-600" />
          </div>
        </div>
      </div>

      {/* Location Performance Table */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-gray-200">
              <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Localisation</th>
              <th className="text-right py-3 px-4 text-sm font-semibold text-gray-700">Commandes</th>
              <th className="text-right py-3 px-4 text-sm font-semibold text-gray-700">Dépôts</th>
              <th className="text-right py-3 px-4 text-sm font-semibold text-gray-700">Revenu</th>
              <th className="text-right py-3 px-4 text-sm font-semibold text-gray-700">Vélocité</th>
              <th className="text-right py-3 px-4 text-sm font-semibold text-gray-700">Utilisation</th>
            </tr>
          </thead>
          <tbody>
            {sortedData?.map((location, index) => (
              <tr key={index} className="border-b border-gray-100 hover:bg-gray-50 transition-colors">
                <td className="py-3 px-4">
                  <div className="flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-gray-400" />
                    <span className="font-medium text-gray-900">{location?.location}</span>
                  </div>
                </td>
                <td className="text-right py-3 px-4 text-gray-700">{location?.orders}</td>
                <td className="text-right py-3 px-4 text-gray-700">{location?.deposits}</td>
                <td className="text-right py-3 px-4 font-semibold text-green-600">
                  €{location?.revenue?.toFixed(2)}
                </td>
                <td className="text-right py-3 px-4">
                  <div className="flex items-center justify-end gap-1">
                    <span className="text-gray-700">{location?.stockVelocity?.toFixed(2)}</span>
                    <span className="text-xs text-gray-500">/jour</span>
                  </div>
                </td>
                <td className="text-right py-3 px-4">
                  <div className="flex items-center justify-end gap-2">
                    <div className="w-16 h-2 bg-gray-200 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-green-500 rounded-full transition-all duration-300"
                        style={{ width: `${Math.min(location?.capacityUtilization || 0, 100)}%` }}
                      ></div>
                    </div>
                    <span className="text-sm text-gray-700 w-12">
                      {Math.round(location?.capacityUtilization || 0)}%
                    </span>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Heat Map Visualization */}
      <div className="mt-6 pt-6 border-t border-gray-200">
        <h3 className="text-sm font-semibold text-gray-700 mb-4">Carte de Chaleur Performance</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {sortedData?.slice(0, 8)?.map((location, index) => {
            const intensity = (location?.revenue / totalRevenue) * 100;
            return (
              <div
                key={index}
                className="rounded-lg p-3 transition-all duration-300 hover:scale-105 cursor-pointer"
                style={{
                  backgroundColor: `rgba(34, 197, 94, ${Math.max(0.1, intensity / 100)})`,
                  border: '1px solid rgba(34, 197, 94, 0.3)'
                }}
              >
                <p className="text-xs font-medium text-gray-800 truncate">{location?.location}</p>
                <p className="text-lg font-bold text-gray-900 mt-1">{location?.orders}</p>
                <p className="text-xs text-gray-600">€{location?.revenue?.toFixed(0)}</p>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}